import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { dayToDate } from "../../../../../app/src/Utils/agendaDateUtils";

// Textos de fecha de la Agenda en el idioma activo: casillas de la tira ("LUN 28 SEP"), título de
// cada día ("Lunes 28 de septiembre"), aria-label de la casilla y hora de la tarjeta (HH:mm).
// Los días (YYYY-MM-DD) son fechas de calendario: se formatean en UTC a mediodía (dayToDate) para
// que ninguna zona horaria las cambie de día.

const safeFormatter = (lang, options) => {
  try {
    return new Intl.DateTimeFormat(lang, { ...options, timeZone: "UTC" });
  } catch (e) {
    return new Intl.DateTimeFormat("es", { ...options, timeZone: "UTC" });
  }
};

const short = (value) => value.replace(/\./g, "").slice(0, 3).toUpperCase();
const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

export const useAgendaFormat = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "es";

  return useMemo(() => {
    const weekdayShort = safeFormatter(lang, { weekday: "short" });
    const weekdayLong = safeFormatter(lang, { weekday: "long" });
    const monthShort = safeFormatter(lang, { month: "short" });
    const monthLong = safeFormatter(lang, { month: "long" });

    const parts = (day) => {
      const date = dayToDate(day);
      return { weekday: weekdayLong.format(date), day: Number(day.slice(8, 10)), month: monthLong.format(date) };
    };

    /** "lunes 28 de septiembre" (en minúscula, para dentro de una frase). */
    const dayPhrase = (day) => t("agenda.dayTitle", parts(day));

    return {
      tileWeekday: (day) => short(weekdayShort.format(dayToDate(day))),
      tileMonth: (day) => short(monthShort.format(dayToDate(day))),
      tileNumber: (day) => Number(day.slice(8, 10)),
      events: (count) => t("agenda.events", { count }),
      dayPhrase,
      /** "Lunes 28 de septiembre" (título del bloque del día). */
      dayTitle: (day) => capitalize(dayPhrase(day)),
      /** aria-label de la casilla: "lunes 28, 3 eventos". */
      tileAria: (day, count) =>
        t("agenda.tileAria", { weekday: parts(day).weekday, day: parts(day).day, events: t("agenda.events", { count }) }),
      /** HH:mm en la zona del navegador. */
      time: (seconds) => {
        const date = new Date(seconds * 1000);
        return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
      },
    };
  }, [t, lang]);
};
